import { useState, useEffect, useCallback } from 'react';
import { Loader2, Play, CheckCircle2, XCircle, Clock, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('wtf_admin_token');
  return { Authorization: `Bearer ${token}` };
}

const STATUS_STYLE = {
  queued:  { bg: 'bg-gray-50',    text: 'text-gray-400', icon: Clock,         label: 'Queued' },
  running: { bg: 'bg-blue-50',    text: 'text-blue-600', icon: Loader2,       label: 'Running', spin: true },
  done:    { bg: 'bg-green-50',   text: 'text-green-600', icon: CheckCircle2, label: 'Done' },
  failed:  { bg: 'bg-red-50',     text: 'text-red-600',  icon: XCircle,       label: 'Failed' },
};

export function RunPipelineNow() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/pipeline/status`, { headers: authHeaders() });
      if (r.ok) setStatus(await r.json());
    } catch { /* ignore */ }
  }, []);

  // Poll every 3s while running, every 30s when idle
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, status?.running ? 3000 : 30000);
    return () => clearInterval(interval);
  }, [status?.running, fetchStatus]);

  const trigger = async () => {
    setStarting(true);
    try {
      await fetch(`${API}/api/admin/pipeline/run-now`, { method: 'POST', headers: authHeaders() });
      await new Promise(r => setTimeout(r, 500));
      await fetchStatus();
    } finally {
      setStarting(false);
    }
  };

  const phases = status?.phases || [];
  const running = status?.running;
  const completed = phases.length > 0 && !running;
  const totalDuration = phases.reduce((s, p) => s + (p.duration_s || 0), 0);
  const donePhases = phases.filter(p => p.status === 'done').length;
  const failedPhases = phases.filter(p => p.status === 'failed').length;

  return (
    <div data-testid="run-pipeline-now" className="bg-white rounded-2xl border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-amber-500" />
          <p className="text-sm font-black text-gray-800 uppercase tracking-wider">Full Pipeline Refresh</p>
        </div>
        <button
          data-testid="run-pipeline-btn"
          onClick={trigger}
          disabled={running || starting}
          className={`text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all
            ${running || starting
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'}`}
        >
          {running || starting ? (
            <>
              <Loader2 size={12} className="animate-spin" /> {running ? 'Running...' : 'Starting...'}
            </>
          ) : (
            <>
              <Play size={12} className="fill-current" /> Run Pipeline Now
            </>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Triggers all 8 phases: RSS + X/Twitter, Google Trends, Apify scrapers, Structured APIs, TikTok Shop, AI pre-gen, Pulse pipeline, Trends v2 scoring. Typical runtime: 25–40 min.
      </p>

      {phases.length === 0 ? (
        <div className="text-xs text-gray-400 italic py-3 text-center">No refresh has been triggered yet.</div>
      ) : (
        <div className="space-y-2">
          {/* Summary bar */}
          <div className="flex items-center justify-between text-[11px] font-semibold px-3 py-2 rounded-lg bg-gray-50">
            <span className="text-gray-600">
              {running ? `Running phase ${donePhases + 1}/${phases.length}` : completed ? `Completed: ${donePhases}/${phases.length} phases` : 'Idle'}
              {failedPhases > 0 && <span className="text-red-500 ml-2">· {failedPhases} failed</span>}
            </span>
            <span className="text-gray-500 font-mono">
              {totalDuration > 0 && `${totalDuration.toFixed(1)}s`}
              {status?.started_at && !running && ` · last run ${new Date(status.started_at).toLocaleTimeString()}`}
            </span>
          </div>

          {/* Phase grid */}
          <div className="space-y-1">
            {phases.map((p, i) => {
              const style = STATUS_STYLE[p.status] || STATUS_STYLE.queued;
              const Icon = style.icon;
              return (
                <div key={p.name} data-testid={`pipeline-phase-${p.name}`} className={`${style.bg} rounded-lg px-3 py-2 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Icon size={13} className={`${style.text} ${style.spin ? 'animate-spin' : ''}`} />
                    <span className="text-[11px] text-gray-400 font-mono w-5">{i + 1}.</span>
                    <span className="text-xs font-semibold text-gray-800">{p.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.duration_s > 0 && (
                      <span className="text-[10px] font-mono text-gray-500">{p.duration_s.toFixed(1)}s</span>
                    )}
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${style.text}`}>
                      {style.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error from any failed phase */}
          {phases.some(p => p.error) && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
              {phases.filter(p => p.error).map(p => (
                <p key={p.name} className="text-[10px] text-red-500"><span className="font-bold">{p.label}:</span> {p.error}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
